import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiInbox } from 'react-icons/fi';
import ClientForm from '../components/shared/ClientForm';
import { appendLicenseFields, toYmd } from '../components/shared/clientFormConfig';
import { Badge, EmptyState, PageHeader, Skeleton, StatusPill, toast } from '../components/ui';
import { usePageCrumbs } from '../components/shell';
import { addEntreprise, downloadFile } from '../servicesV2/entrepriseService';
import { approveClientRequest, getClientRequestById } from '../servicesV2/partnerService';

function CreateClientFromRequest() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [request, setRequest] = useState(null);
  const [initialLogoUrl, setInitialLogoUrl] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');

  usePageCrumbs([
    { label: 'Requests', to: '/client-requests' },
    { label: request?.company_name || 'Demande' },
    { label: 'Créer le client' },
  ]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await getClientRequestById(requestId);
        if (cancelled) return;
        setRequest(res);
        // TODO: re-enable once /public/download is fixed for the "master" pseudo-tenant.
        // Currently returns 401 ("Invalid tenant") which trips the global logout interceptor.
        // if (res.logo) {
        //   try {
        //     const url = await downloadFile(res.logo);
        //     if (!cancelled) setInitialLogoUrl(url);
        //   } catch (err) {
        //     console.error('Logo fetch failed:', err);
        //   }
        // }
      } catch (err) {
        console.error(err);
        toast.error('Chargement impossible', { description: err?.message });
      } finally {
        if (!cancelled) setFetching(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const handleSubmit = async ({ data, modules, keptModules, color, logo }) => {
    setLoading(true);
    setErrorText('');
    try {
      const formData = new FormData();
      formData.append('Activite', data.Activite);
      formData.append('DomainApplication', data.DomainApplication);
      formData.append('JustificationExclusion', data.JustificationExclusion || '');
      formData.append('Key', data.Key);
      formData.append('Nom', data.Nom);
      formData.append('RaisonSocial', data.RaisonSocial);
      // Plain YYYY-MM-DD: toISOString() shifted the day back in UTC+1, and the
      // licence signs exactly these dates.
      formData.append('endDate', toYmd(data.endDate));
      formData.append('representantEmail', data.representantEmail);
      formData.append('representantName', data.representantName);
      formData.append('startDate', toYmd(data.startDate));
      formData.append('color', color || '');
      formData.append('isOnPremise', data.isOnPremise ? 'true' : 'false');
      if (data.isOnPremise) formData.append('publicApiUrl', data.publicApiUrl || '');
      formData.append('client_request_id', requestId);
      if (logo) formData.append('PieceJointe', logo);
      modules.forEach((m) => formData.append('modules[]', m));
      appendLicenseFields(formData, data, keptModules);

      await addEntreprise(formData);

      // Auto-approve the request on success (kept from the original flow).
      try {
        await approveClientRequest(requestId);
      } catch (err) {
        console.error('Auto-approve failed:', err);
      }

      toast.success('Client provisionné', { description: data.Nom });
      navigate('/client-requests');
    } catch (err) {
      const serverKey = err?.response?.data?.message;
      const message = serverKey
        ? t(`ERRORS.${serverKey}`, { defaultValue: serverKey })
        : t('ERRORS.ERROR', { defaultValue: 'Une erreur est survenue.' });
      setErrorText(message);
      toast.error('Échec', { description: message });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <>
        <PageHeader title="Chargement…" />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          <Skeleton className="hidden h-60 rounded-md lg:block" />
          <div className="flex flex-col gap-4">
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-60 rounded-lg" />
          </div>
        </div>
      </>
    );
  }

  if (!request) {
    return (
      <>
        <PageHeader title="Demande introuvable" />
        <EmptyState
          variant="danger"
          icon={<FiInbox size={22} />}
          title="Impossible de charger cette demande"
          description="Elle a peut-être été supprimée ou l'URL est incorrecte."
        />
      </>
    );
  }

  const partnerName = request.partner_name || request.partner?.company_name || '—';
  const modulesFromRequest = Array.isArray(request.requested_modules)
    ? request.requested_modules
    : [];

  const summary = (
    <div className="rounded-lg border border-line bg-surface shadow-e1 p-3">
      <p className="text-micro text-fg-subtle">Demande</p>
      <p className="mt-1 truncate text-body-sm font-semibold text-fg">
        {request.company_name}
      </p>
      <p className="mt-0.5 truncate text-caption text-fg-muted">via {partnerName}</p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <StatusPill status={request.status} size="sm" />
        {request.key && (
          <Badge variant="mono" size="sm">
            {request.key}
          </Badge>
        )}
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Provisionner un client"
        description={`Depuis la demande de ${partnerName}.`}
      />
      <ClientForm
        mode="from-request"
        initialValues={{
          Nom: request.company_name || '',
          Key: request.key || '',
          representantEmail: request.contact_email || '',
          modules: modulesFromRequest,
          color: request.color || '',
        }}
        initialLogoUrl={initialLogoUrl}
        showColor
        submitLabel="Provisionner et approuver"
        onSubmit={handleSubmit}
        onCancel={() => navigate('/client-requests')}
        errorText={errorText}
        loading={loading}
        summary={summary}
      />
    </>
  );
}

export default CreateClientFromRequest;
